/**
 * FunnelSection — Bezoeker → Klik → Call funnel + match rate + top pagina's
 * Gebruikt visitor_sessions en phone_clicks uit Supabase.
 */

import React, { useState } from 'react';
import { Users, MousePointer, Phone, CheckCircle, Euro, TrendingUp, Link, Globe, Compass, Info, X, Sparkles, Bot } from 'lucide-react';
import { formatCurrency } from '../../utils/dashboardUtils';
import { formatPageUrl } from '../../utils/trackingHelpers';
import { formatNumber } from '../../utils/dashboardUtils';

// Match-methodes — gesorteerd van hoogste naar laagste confidence.
// Kleuren via inline tokens (sluit aan op bestaande --color-success / --color-warning / --color-brand).
const MATCH_METHODS = [
  {
    key: 'timestamp_phone',
    label: 'Klik + nummer',
    confidence: 95,
    color: '#16a34a',        // groen — sterkste
    bg: '#dcfce7',
    description: 'Bezoeker klikte de tel:-link én het nummer klopt exact.',
  },
  {
    key: 'session_daily_unique',
    label: 'Enige bezoeker die dag',
    confidence: 85,
    color: '#22c55e',        // lichtgroen
    bg: '#ecfdf5',
    description: 'Maar één bezoeker op die site die dag — vrijwel zeker dezelfde persoon.',
  },
  {
    key: 'timestamp',
    label: 'Klik op site',
    confidence: 80,
    color: '#2563eb',        // blauw
    bg: '#dbeafe',
    description: 'Bezoeker klikte een bel-knop op de juiste site binnen 5 minuten.',
  },
  {
    key: 'timestamp_recurring',
    label: 'Terugkerende beller',
    confidence: 75,
    color: '#475569',        // blauw-grijs
    bg: '#e2e8f0',
    description: 'Beller heeft het nummer opgeslagen en belt terug — geen websitebezoek verwacht.',
  },
  {
    key: 'session_only',
    label: 'Sessie binnen 30 min',
    confidence: 60,
    color: '#f97316',        // oranje
    bg: '#ffedd5',
    description: 'Bezoeker was op de site, maar klikte niet op het nummer.',
  },
  {
    key: 'session_pool',
    label: 'Sessie in regio',
    confidence: 40,
    color: '#fb923c',        // oranje-licht
    bg: '#fff7ed',
    description: 'Meerdere sites delen één nummer (bijv. Florida). Sessie gevonden in de regio.',
  },
  {
    key: 'session_daily_multiple',
    label: 'Meerdere bezoekers',
    confidence: 30,
    color: '#ef4444',        // rood-oranje
    bg: '#fee2e2',
    description: 'Meerdere bezoekers die dag; dichtstbijzijnde sessie gekozen.',
  },
  {
    key: 'unmatched',
    label: 'Niet gematcht',
    confidence: null,
    color: '#94a3b8',        // grijs
    bg: '#f1f5f9',
    description: 'Geen websitebezoek gevonden.',
  },
];

const FunnelStep = ({ icon: Icon, label, value, ratio, ratioLabel, isLast }) => (
  <div className="funnel-step-wrapper">
    <div className="kpi-card funnel-step-card">
      <div className="funnel-step-icon">
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <div className="funnel-step-meta">
        <span className="funnel-step-label">{label}</span>
        {ratio !== null && (
          <span className="funnel-step-ratio">{ratio} {ratioLabel}</span>
        )}
      </div>
      <div className="funnel-step-value">{value}</div>
    </div>
    {!isLast && <div className="funnel-arrow">→</div>}
  </div>
);

// Source styling — AI-bronnen (ChatGPT/Perplexity) krijgen eigen kleur + icoon zodat ze opvallen.
const SOURCE_STYLES = {
  'ChatGPT':            { color: '#10a37f', bg: '#d1fae5', icon: Sparkles },   // groen — OpenAI tint
  'Perplexity':         { color: '#1e40af', bg: '#dbeafe', icon: Bot },        // blauw
  'Google Ads':         { color: '#D02E17', bg: '#fef2f2', icon: null },
  'Organisch (Google)': { color: '#2563eb', bg: '#eff6ff', icon: null },
  'Organisch (Bing)':   { color: '#0891b2', bg: '#ecfeff', icon: null },
  'Bing':               { color: '#0891b2', bg: '#ecfeff', icon: null },
  'Organisch (DuckDuckGo)': { color: '#f59e0b', bg: '#fffbeb', icon: null },
  'DuckDuckGo':         { color: '#f59e0b', bg: '#fffbeb', icon: null },
  'Direct':             { color: '#475569', bg: '#f1f5f9', icon: null },
  'Referral':           { color: '#7c3aed', bg: '#f5f3ff', icon: null },
};

const getSourceStyle = (source) =>
  SOURCE_STYLES[source] || { color: 'var(--color-brand-600, #D02E17)', bg: 'var(--color-neutral-100)', icon: null };

const FunnelSection = ({ funnelData, topPages, matchBreakdown, topLandingPages, sourceAttribution, loading, calls = 0, leads = 0, revenue = 0 }) => {
  const [showDiagnose, setShowDiagnose] = useState(false);
  const [showMatchInfo, setShowMatchInfo] = useState(false);
  const [showAllLandingPages, setShowAllLandingPages] = useState(false);

  if (loading) {
    return (
      <div className="mb-6 lg:mb-8">
        <div style={{ color: 'var(--color-neutral-500)', padding: '16px', textAlign: 'center', fontSize: '0.875rem' }}>
          Laden…
        </div>
      </div>
    );
  }

  const { sessions = 0, clicks = 0, matchedCalls = 0, totalCalls = 0 } = funnelData || {};

  const clickRatio   = sessions > 0  ? `${((clicks / sessions) * 100).toFixed(1)}%` : null;
  const callRatio    = clicks > 0    ? `${((calls / clicks) * 100).toFixed(1)}%`   : null;
  const leadRatio    = calls > 0     ? `${((leads / calls) * 100).toFixed(1)}%`    : null;
  const revenueRatio = leads > 0     ? `€${((revenue / leads)).toFixed(0)}`        : null;
  const matchRate    = totalCalls > 0 ? ((matchedCalls / totalCalls) * 100).toFixed(0) : '—';

  return (
    <div className="mb-6 lg:mb-8">
      {/* Funnel steps */}
      <div className="funnel-row">
        <FunnelStep
          icon={Users}
          label="SESSIES"
          value={formatNumber(sessions)}
          ratio={null}
          ratioLabel=""
          isLast={false}
        />
        <FunnelStep
          icon={MousePointer}
          label="TEL-KLIKKEN"
          value={formatNumber(clicks)}
          ratio={clickRatio}
          ratioLabel="van sessies"
          isLast={false}
        />
        <FunnelStep
          icon={Phone}
          label="CALLS"
          value={formatNumber(calls)}
          ratio={callRatio}
          ratioLabel="van klikken"
          isLast={false}
        />
        <FunnelStep
          icon={CheckCircle}
          label="LEADS"
          value={formatNumber(leads)}
          ratio={leadRatio}
          ratioLabel="van calls"
          isLast={false}
        />
        <FunnelStep
          icon={Euro}
          label="OMZET"
          value={formatCurrency(revenue)}
          ratio={revenueRatio}
          ratioLabel="per lead"
          isLast={true}
        />
      </div>

      {/* Funnel diagnose (collapsible) */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={() => setShowDiagnose(prev => !prev)}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            color: 'var(--color-neutral-600)',
          }}
        >
          <span>{showDiagnose ? '▼' : '▶'}</span>
          <span>FUNNEL DIAGNOSE</span>
        </button>

        {showDiagnose && (
          <>
            {/* Match rate bar */}
            <div className="table-container" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', marginTop: '8px' }}>
              <TrendingUp size={16} style={{ color: 'var(--color-neutral-500)', flexShrink: 0 }} />
              <span style={{ fontSize: '0.8rem', color: 'var(--color-neutral-600)', flexShrink: 0 }}>
                Attributie match rate
              </span>
              <div style={{ flex: 1, height: '6px', background: 'var(--color-neutral-200)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(parseFloat(matchRate) || 0, 100)}%`,
                  background: parseFloat(matchRate) >= 50 ? 'var(--color-success, #22c55e)' : 'var(--color-warning, #f59e0b)',
                  borderRadius: '3px',
                  transition: 'width 0.4s ease'
                }} />
              </div>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-neutral-800)', flexShrink: 0 }}>
                {matchRate}%
              </span>
              <span style={{ fontSize: '0.75rem', color: 'var(--color-neutral-500)', flexShrink: 0 }}>
                ({matchedCalls}/{totalCalls} calls met visitor_id)
              </span>
            </div>

            {/* B1 — Match method breakdown (uitgebreid + info tooltip) */}
            {matchBreakdown && (() => {
              const totalMatch = MATCH_METHODS.reduce((s, m) => s + (matchBreakdown[m.key] || 0), 0) || 1;
              return (
                <div className="table-container" style={{ marginBottom: '16px' }}>
                  {/* Header met info-icoon */}
                  <div style={{
                    padding: '10px 16px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderBottom: '1px solid var(--color-neutral-200)',
                    position: 'relative',
                  }}>
                    <TrendingUp size={14} style={{ color: 'var(--color-neutral-500)' }} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
                      ATTRIBUTIE KWALITEIT
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowMatchInfo(prev => !prev)}
                      aria-label="Uitleg call attribution"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: showMatchInfo ? 'var(--color-brand-600, #D02E17)' : 'var(--color-neutral-500)',
                      }}
                    >
                      <Info size={14} />
                    </button>
                    <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--color-neutral-500)' }}>
                      {formatNumber(totalMatch)} calls
                    </span>
                  </div>

                  {/* Info paneel — inline uitklapbaar (mobiel-vriendelijk) */}
                  {showMatchInfo && (
                    <div style={{
                      padding: '14px 16px',
                      background: 'var(--color-neutral-50, #f8fafc)',
                      borderBottom: '1px solid var(--color-neutral-200)',
                      fontSize: '0.8rem',
                      color: 'var(--color-neutral-700)',
                      lineHeight: 1.55,
                      position: 'relative',
                    }}>
                      <button
                        type="button"
                        onClick={() => setShowMatchInfo(false)}
                        aria-label="Sluit uitleg"
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--color-neutral-500)',
                          padding: '2px',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        <X size={14} />
                      </button>
                      <div style={{ fontWeight: 700, marginBottom: '6px', color: 'var(--color-neutral-800)' }}>
                        Hoe werkt call attribution?
                      </div>
                      <div style={{ marginBottom: '10px', color: 'var(--color-neutral-600)' }}>
                        Elke oproep in de leads-tabel koppelen we aan een websitebezoek. De methode bepaalt hoe zeker we zijn:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {MATCH_METHODS.map(m => (
                          <div key={m.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <span style={{
                              flex: '0 0 10px',
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              background: m.color,
                              marginTop: '5px',
                            }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontWeight: 600, color: 'var(--color-neutral-800)' }}>
                                {m.label}
                              </span>
                              {m.confidence !== null && (
                                <span style={{ fontWeight: 600, color: m.color, marginLeft: '6px' }}>
                                  ({m.confidence}%)
                                </span>
                              )}
                              <div style={{ color: 'var(--color-neutral-600)', marginTop: '2px' }}>
                                {m.description}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Per-methode rijen — compact, mobile-first: badge+conf links, count rechts, bar daaronder */}
                  <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {MATCH_METHODS.map(m => {
                      const count = matchBreakdown[m.key] || 0;
                      const pct = (count / totalMatch) * 100;
                      // Verberg "Klik op site" (timestamp, 80%) wanneer count === 0
                      if (m.key === 'timestamp' && count === 0) return null;
                      return (
                        <div key={m.key} className="match-method-row">
                          {/* Top regel: dot + label (links), count + percentage (rechts) */}
                          <div className="match-method-top">
                            <span className="match-method-badge">
                              <span className="match-method-dot" style={{ background: m.color }} />
                              <span className="match-method-label">{m.label}</span>
                            </span>
                            <span className="match-method-count">
                              {formatNumber(count)} <span className="match-method-pct">({pct.toFixed(0)}%)</span>
                            </span>
                          </div>
                          {/* Bar daaronder, full-width */}
                          <div className="match-method-bar-track">
                            <div
                              className="match-method-bar-fill"
                              style={{ width: `${pct}%`, background: m.color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* B3 — Source attributie */}
            {sourceAttribution && sourceAttribution.length > 0 && (() => {
              const totalSrc = sourceAttribution.reduce((s, r) => s + r.count, 0) || 1;
              return (
                <div className="table-container" style={{ marginBottom: '16px' }}>
                  <div style={{ padding: '10px 16px 8px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--color-neutral-200)' }}>
                    <Compass size={14} style={{ color: 'var(--color-neutral-500)' }} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
                      SOURCE ATTRIBUTIE
                    </span>
                  </div>
                  <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {sourceAttribution.map((row, i) => {
                      const pct = (row.count / totalSrc) * 100;
                      const style = getSourceStyle(row.source);
                      const Icon = style.icon;
                      return (
                        <div key={i} className="source-attr-row">
                          <div className="source-attr-top">
                            <span className="source-attr-badge">
                              <span className="source-attr-dot" style={{ background: style.color }} />
                              {Icon && <Icon size={12} strokeWidth={2.5} style={{ color: style.color, flexShrink: 0 }} />}
                              <span className="source-attr-label">{row.source}</span>
                            </span>
                            <span className="source-attr-count">
                              {formatNumber(row.count)} <span className="source-attr-pct">({pct.toFixed(0)}%)</span>
                            </span>
                          </div>
                          <div className="source-attr-bar-track">
                            <div
                              className="source-attr-bar-fill"
                              style={{ width: `${pct}%`, background: style.color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* B2 — Top landing pages → calls */}
            {topLandingPages && topLandingPages.length > 0 && (() => {
              const visiblePages = showAllLandingPages ? topLandingPages : topLandingPages.slice(0, 5);
              const hasMore = topLandingPages.length > 5;
              return (
                <div className="table-container" style={{ marginBottom: '16px' }}>
                  <div style={{ padding: '10px 16px 8px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--color-neutral-200)' }}>
                    <Globe size={14} style={{ color: 'var(--color-neutral-500)' }} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
                      TOP LANDING PAGES → CALLS
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="table-premium">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>LANDING PAGE</th>
                          <th style={{ textAlign: 'right' }}>CALLS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visiblePages.map((page, i) => (
                          <tr key={i}>
                            <td>
                              <span className="rank-badge">{i + 1}</span>
                            </td>
                            <td>
                              <a
                                href={page.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cell-primary landing-page-link"
                                title={page.url}
                              >
                                {formatPageUrl(page.url, 40)}
                              </a>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <span className="cell-brand">{page.count}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {hasMore && (
                    <div style={{ padding: '8px 16px', borderTop: '1px solid var(--color-neutral-100)', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => setShowAllLandingPages(prev => !prev)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: 'var(--color-brand-600, #D02E17)',
                          padding: '4px 8px',
                        }}
                      >
                        {showAllLandingPages
                          ? 'Toon minder'
                          : `Toon meer (${topLandingPages.length - 5})`}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Top pagina's */}
            {topPages && topPages.length > 0 && (
              <div className="table-container">
                <div style={{ padding: '10px 16px 8px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--color-neutral-200)' }}>
                  <Link size={14} style={{ color: 'var(--color-neutral-500)' }} />
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
                    TOP PAGINA'S (unieke klikkers)
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="table-premium">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>PAGINA</th>
                        <th style={{ textAlign: 'right' }}>KLIKKEN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPages.map((page, i) => (
                        <tr key={i}>
                          <td>
                            <span className="rank-badge">{i + 1}</span>
                          </td>
                          <td>
                            <span className="cell-primary" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {formatPageUrl(page.url)}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <span className="cell-brand">{page.count}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FunnelSection;
